import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useDTicketSubscription } from '@mopla/business-logic';
import {
	calculateEditedPauseDates,
	getConditionsToHidePauseOptions,
	getPauseDurationInMonths,
	isPauseStartInvalidAfterTenth,
	ISubscriptionPauseFormValues,
	TSubscriptionPauseDuration,
} from '@mopla/utils';

export const useDTicketSubscriptionEditPauseForm = () => {
	const { subscription } = useDTicketSubscription();
	const {
		control,
		handleSubmit,
		watch,
		reset,
		formState: { isDirty },
	} = useForm<ISubscriptionPauseFormValues>({
		defaultValues: {
			duration: null,
		},
	});

	const lastSavedDuration = useMemo(
		() =>
			subscription?.pauseStart && subscription?.pauseEnd
				? getPauseDurationInMonths(
						subscription.pauseStart,
						subscription.pauseEnd
				  )
				: null,
		[subscription?.pauseStart, subscription?.pauseEnd]
	);

	useEffect(() => {
		if (!lastSavedDuration) return;

		reset({
			duration: lastSavedDuration,
		});
	}, [lastSavedDuration, reset]);

	const pauseOptionConditions = useMemo(() => {
		if (!subscription?.pauseStart) return;

		return getConditionsToHidePauseOptions(subscription.pauseStart);
	}, [subscription?.pauseStart]);

	const canReschedule = useMemo(() => {
		if (!subscription?.pauseStart) return true;
		return !isPauseStartInvalidAfterTenth(subscription.pauseStart);
	}, [subscription?.pauseStart]);

	const selectedDuration = watch('duration');

	const pauseDates = useMemo(() => {
		if (
			!subscription?.pauseStart ||
			!subscription?.pauseEnd ||
			!selectedDuration
		)
			return;

		return calculateEditedPauseDates(
			parseInt(selectedDuration, 10),
			subscription?.pauseStart,
			subscription?.pauseEnd
		);
	}, [subscription?.pauseStart, subscription?.pauseEnd, selectedDuration]);

	const onSubmit = (
		callback: (duration: TSubscriptionPauseDuration) => void
	) => {
		return handleSubmit(({ duration }) => {
			if (!duration) return;
			callback(duration);
		});
	};

	return {
		control,
		selectedDuration,
		pauseDates,
		isThirdMonthOfPause: pauseOptionConditions?.isThirdMonthOfPause,
		monthsPassed: pauseOptionConditions?.monthsPassed,
		canReschedule,
		isDirty,
		onSubmit,
	};
};
